import { K_BLACK, WHITE, rgb, type Color } from './color.js';
import { tentFlat, tentPagePanels } from './tent.js';
import type { Element, GridStyle, HolidayListEl, ImageEl, KeyEl, LogoEl, PageDesign, Product, Rect, TextEl, TextStyle, YearGridEl, YearStyle } from './schema.js';

export interface PageSize {
  w: number;
  h: number;
}

/** Curated, embeddable (SIL OFL 1.1) font ids shipped with the app. */
export const CURATED_FONTS: { id: string; family: string; weight: number; file: string }[] = [
  ['Inter', 'Inter', [400, 600, 700]],
  ['Montserrat', 'Montserrat', [400, 600, 700]],
  ['Playfair Display', 'PlayfairDisplay', [400, 600, 700]],
  ['Lato', 'Lato', [400, 700]],
  ['Merriweather', 'Merriweather', [400, 700]],
  ['Oswald', 'Oswald', [400, 600, 700]],
  ['Dancing Script', 'DancingScript', [400, 600, 700]],
].flatMap(([family, file, weights]) =>
  (weights as number[]).map((w) => ({
    id: `${(family as string).toLowerCase().replace(/ /g, '-')}-${w}`,
    family: family as string,
    weight: w,
    file: `${file}_${w === 400 ? '400Regular' : w === 600 ? '600SemiBold' : '700Bold'}.ttf`,
  })),
);

export const ts = (fontId: string, sizePt: number, color: Color, align: TextStyle['align'] = 'left', extra: Partial<TextStyle> = {}): TextStyle => ({
  fontId,
  sizePt: +Math.max(3, sizePt).toFixed(2), // templates never go below 3 pt
  color,
  align,
  letterSpacingPt: 0,
  lineHeight: 1.2,
  uppercase: false,
  ...extra,
});

const GREY_WEEKEND = rgb('#e6e6e6');

export function defaultGridStyle(scale = 1, headFont = 'montserrat-600', bodyFont = 'inter-400'): GridStyle {
  return {
    outerBorder: { widthPt: 0.75, color: K_BLACK },
    innerLines: { widthPt: 0.35, color: K_BLACK },
    cellFill: null,
    cellPaddingMm: 1.5 * scale,
    datePosition: 'top-left',
    header: {
      heightMm: 7 * scale,
      fill: rgb('#1f2a37'),
      text: ts(headFont, 8.5 * scale, WHITE, 'center', { uppercase: true, letterSpacingPt: 0.6 }),
      labels: 'short',
    },
    dateText: ts(bodyFont.replace(/-\d+$/, '-600'), 10 * scale, K_BLACK, 'left'),
    adjacentText: ts(bodyFont, 9 * scale, rgb('#a0a0a0'), 'left'),
    eventText: ts(bodyFont, 5.5 * scale, rgb('#b3261e'), 'left', { lineHeight: 1.1 }),
    saturday: { fill: GREY_WEEKEND, textColor: null },
    sunday: { fill: GREY_WEEKEND, textColor: null },
    weekNumbers: { text: ts(bodyFont, Math.max(3, 6 * scale), rgb('#808080'), 'center'), widthMm: +Math.max(3, 6 * scale).toFixed(2) },
  };
}

export interface TemplateInfo {
  id: string;
  name: string;
  description: string;
  /** Which product the template lays out: month pages or a single year-at-a-glance page. */
  product: Product['productType'];
  /** Business, personal, estate agent … (for grouping in the picker). */
  audience: 'general' | 'business' | 'personal' | 'estate-agent' | 'custom';
  /** Calendar area applied when the template is chosen (monthly templates). */
  defaultArea: Product['calendarArea'];
}
export const TEMPLATES: TemplateInfo[] = [
  { id: 'photo-top', name: 'Photograph above, calendar below', description: 'One large photograph with the month grid underneath. The classic wall calendar.', product: 'monthly', audience: 'personal', defaultArea: 'bottom' },
  { id: 'full-grid', name: 'Full-page calendar grid', description: 'A large planner grid filling the page, with roomy day cells and previous/next month minis. No photograph.', product: 'monthly', audience: 'general', defaultArea: 'full' },
  { id: 'collage', name: 'Photo collage above the calendar', description: 'Three photographs arranged as a collage above the grid.', product: 'monthly', audience: 'personal', defaultArea: 'bottom' },
  { id: 'background-photo', name: 'Background photograph with calendar panel', description: 'Full-bleed photograph behind a readable translucent calendar panel.', product: 'monthly', audience: 'personal', defaultArea: 'bottom' },
  { id: 'business', name: 'Business: photo and contact band', description: 'Monthly photograph and grid with a branded band of logo, company name, phone, email and website on every page.', product: 'monthly', audience: 'business', defaultArea: 'bottom' },
  { id: 'estate-agent', name: 'Estate agent', description: 'Monthly photograph (e.g. the client’s home) with the agent’s portrait, name, phone and agency logo on every page.', product: 'monthly', audience: 'estate-agent', defaultArea: 'bottom' },
  { id: 'desk-photo-left', name: 'Desk: photograph beside the calendar', description: 'Landscape desk page: photograph on the left, month grid on the right. Suits wiro desk tents (A5 landscape, 210 × 100, 150 × 150).', product: 'monthly', audience: 'personal', defaultArea: 'bottom' },
  { id: 'desk-pad', name: 'Desk pad / blotter', description: 'Large daily planner grid with previous/next mini months and a notes column. For A3/A2 desk pads (one sheet per month).', product: 'monthly', audience: 'business', defaultArea: 'full' },
  { id: 'year-business', name: 'Year planner: business', description: 'All 12 months with the logo, tagline, phone pill and web/email up front. Optional photo strip. Ideal for DL/A6 magnets and cards.', product: 'year', audience: 'business', defaultArea: 'full' },
  { id: 'year-photo', name: 'Year planner: photo header', description: 'A photograph or brand image across the top, all 12 months, holiday key and a coloured contact band.', product: 'year', audience: 'business', defaultArea: 'full' },
  { id: 'year-estate-agent', name: 'Year planner: estate agent', description: 'Agent portrait, name, phone and agency logo in a header band, an optional “your home” photo, then all 12 months.', product: 'year', audience: 'estate-agent', defaultArea: 'full' },
  { id: 'year-personal', name: 'Year planner: personal photo', description: 'A family photo with a name in script lettering above all 12 months.', product: 'year', audience: 'personal', defaultArea: 'full' },
  { id: 'year-poster', name: 'Year planner: wall poster', description: 'Large year-at-a-glance poster: logo, year and contact details across the top, 12 roomy months below. Suits A3–A0.', product: 'year', audience: 'business', defaultArea: 'full' },
  { id: 'custom-month', name: 'Custom: monthly (blank)', description: 'Starting point for the custom builder: month heading and grid only. Add your own photo, logo and text blocks.', product: 'monthly', audience: 'custom', defaultArea: 'full' },
  { id: 'custom-year', name: 'Custom: year view (blank)', description: 'Starting point for the custom builder: year heading and all 12 months only.', product: 'year', audience: 'custom', defaultArea: 'full' },
  { id: 'custom-half', name: 'Custom: tent (blank)', description: 'Starting point for the custom builder: tent panels with the year heading and their months only.', product: 'tent', audience: 'custom', defaultArea: 'full' },
  { id: 'tent-business', name: 'Tent card: business', description: 'Folded self-standing tent: six months per face beside the logo, phone and web details.', product: 'tent', audience: 'business', defaultArea: 'full' },
  { id: 'tent-photo', name: 'Tent card: photo', description: 'Folded self-standing tent: a photograph beside six months on each face, contact line underneath.', product: 'tent', audience: 'personal', defaultArea: 'full' },
];
export const templateInfo = (id: string): TemplateInfo => TEMPLATES.find((t) => t.id === id) ?? TEMPLATES[0];
export const ACCENT: Color = rgb('#1f3a5f');

interface Ctx {
  W: number;
  H: number;
  top: number; // content top (below binding clearance)
  left: number;
  right: number;
  bottom: number;
  s: number; // type scale relative to A4
  area: Product['calendarArea'];
}

const R = (x: number, y: number, w: number, h: number): Rect => ({ x: +x.toFixed(2), y: +y.toFixed(2), w: +w.toFixed(2), h: +h.toFixed(2) });

type TemplateProduct = Pick<Product, 'safeMarginMm' | 'bindingEdge' | 'bindingClearanceMm' | 'calendarArea'> & Partial<Pick<Product, 'tent' | 'carrier'>>;

function ctxFor(size: PageSize, product: TemplateProduct): Ctx {
  const s = Math.sqrt((size.w * size.h) / (210 * 297));
  // margin from the safe area, plus a little air that shrinks on small cards
  const m = product.safeMarginMm + Math.min(3, 3 * s);
  const bindTop = product.bindingEdge === 'top' || product.bindingEdge === 'top-bottom' ? product.bindingClearanceMm : 0;
  const bindBottom = product.bindingEdge === 'top-bottom' ? product.bindingClearanceMm : 0;
  const bindLeft = product.bindingEdge === 'left' ? product.bindingClearanceMm : 0;
  return {
    W: size.w,
    H: size.h,
    top: Math.max(m, bindTop + Math.min(4, 4 * s)),
    left: Math.max(m, bindLeft + Math.min(4, 4 * s)),
    right: size.w - m,
    bottom: size.h - Math.max(m, bindBottom + Math.min(4, 4 * s)),
    s,
    area: product.calendarArea,
  };
}

function footer(c: Ctx, color: Color = rgb('#555555')): Element {
  return {
    id: 'footer', name: 'Footer', type: 'text', role: 'footer',
    rect: R(c.left, c.bottom - 6 * c.s, c.right - c.left, 6 * c.s),
    text: '{CONTACT}', style: ts('inter-400', 7.5 * c.s, color, 'center'), valign: 'middle', panel: null,
  };
}
function logo(c: Ctx): LogoEl {
  return { id: 'logo', name: 'Logo', type: 'logo', rect: R(c.right - 28 * c.s, c.bottom - 9 * c.s, 28 * c.s, 9 * c.s), align: 'right' };
}
function grid(c: Ctx, rect: Rect, style?: GridStyle): Element {
  return { id: 'grid', name: 'Calendar grid', type: 'grid', rect, style: style ?? defaultGridStyle(c.s) };
}
function heading(c: Ctx, rect: Rect, extra: Partial<TextStyle> = {}, color: Color = K_BLACK): Element {
  return {
    id: 'heading', name: 'Month heading', type: 'text', role: 'heading', rect, text: '{MONTH} {YEAR}',
    // "SEPTEMBER 2027" must fit the box width on narrow pages
    style: ts('montserrat-700', 24 * c.s, color, 'left', { uppercase: true, letterSpacingPt: 1.5, ...extra, sizePt: Math.min(extra.sizePt ?? 24 * c.s, +((rect.w / 11.5) * (72 / 25.4)).toFixed(2)) }), valign: 'bottom', panel: null,
  };
}
function photo(id: string, slot: number, rect: Rect, radiusMm = 0): ImageEl {
  return { id, name: slot === 0 ? 'Photograph' : `Photograph ${slot + 1}`, type: 'image', slot, rect, radiusMm, border: null, required: true };
}
function caption(c: Ctx, rect: Rect, align: TextStyle['align'] = 'right'): Element {
  return {
    id: 'caption', name: 'Caption', type: 'text', role: 'caption', rect, text: '{CAPTION}',
    style: ts('playfair-display-400', 9 * c.s, rgb('#444444'), align), valign: 'top', panel: null,
  };
}

/**
 * Split a content region into the photo region and calendar region according
 * to the chosen calendar area (top half / bottom half / full page).
 */
function split(c: Ctx, photoShare: number) {
  const top = c.top, bottom = c.bottom - 8 * c.s;
  const h = bottom - top;
  if (c.area === 'full') return { photo: null, cal: { y: top, h } };
  if (c.area === 'top') return { cal: { y: top, h: h * 0.5 }, photo: { y: top + h * 0.5 + 4 * c.s, h: h * 0.5 - 4 * c.s } };
  if (c.area === 'bottom') return { photo: { y: top, h: h * 0.5 - 2 * c.s }, cal: { y: top + h * 0.5, h: h * 0.5 } };
  return { photo: { y: top, h: h * photoShare }, cal: { y: top + h * photoShare + 2 * c.s, h: h * (1 - photoShare) - 2 * c.s } };
}

function monthDesign(id: string, c: Ctx): PageDesign {
  const cw = c.right - c.left;
  const headH = 13 * c.s;
  if (Math.min(c.W, c.H) < 75) return compactMonth(id, c);
  switch (id) {
    case 'custom-month': {
      const hh = Math.max(headH, (c.bottom - c.top) * 0.08);
      const gy = c.top + hh + 3 * c.s;
      return { background: null, elements: [heading(c, R(c.left, c.top, cw, hh)), grid(c, R(c.left, gy, cw, c.bottom - gy))] };
    }
    case 'desk-photo-left': {
      const els: Element[] = [];
      const pw = c.area === 'full' ? 0 : cw * 0.42;
      if (pw) els.push(photo('photo', 0, R(c.left, c.top, pw, c.bottom - c.top), 2 * c.s));
      const x = c.left + (pw ? pw + 5 * c.s : 0), w = c.right - x;
      const hh = Math.max(headH, (c.bottom - c.top) * 0.12);
      els.push(heading(c, R(x, c.top, w * 0.62, hh), { sizePt: pt(Math.min(hh * 0.62, (w * 0.62) / 11.5)) }));
      els.push({ id: 'logo', name: 'Logo', type: 'logo', rect: R(x + w * 0.64, c.top, w * 0.36, hh), align: 'right' });
      const footH = Math.max(4, 6 * c.s);
      const gy = c.top + hh + 2 * c.s;
      els.push(grid(c, R(x, gy, w, c.bottom - footH - 1.5 * c.s - gy)));
      els.push({ ...(footer(c) as TextEl), rect: R(x, c.bottom - footH, w, footH) });
      return { background: null, elements: els };
    }
    case 'desk-pad': {
      const els: Element[] = [];
      const hh = 18 * c.s;
      els.push(heading(c, R(c.left, c.top, cw * 0.55, hh), { sizePt: pt(Math.min(hh * 0.62, (cw * 0.55) / 11.5)) }));
      els.push({ id: 'logo', name: 'Logo', type: 'logo', rect: R(c.right - cw * 0.3, c.top, cw * 0.3, hh), align: 'right' });
      const colW = cw * 0.2, gap = 6 * c.s;
      const gw = cw - colW - gap, y = c.top + hh + 4 * c.s;
      const footH = 6 * c.s;
      const gs = defaultGridStyle(c.s);
      gs.dateText.sizePt = 14 * c.s;
      gs.eventText.sizePt = 6.5 * c.s;
      gs.header.fill = ACCENT;
      els.push(grid(c, R(c.left, y, gw, c.bottom - footH - 3 * c.s - y), gs));
      const x = c.left + gw + gap, k = colW / 27;
      const colBottom = c.bottom - footH - 3 * c.s;
      const mh = Math.min(colW * 0.82, (colBottom - y) * 0.32);
      const mini = (which: 'prev' | 'next', my: number): Element => ({ id: `mini-${which}`, name: which === 'prev' ? 'Previous month' : 'Next month', type: 'mini', which, rect: R(x, my, colW, mh), title: ts('inter-700', 5.5 * k, K_BLACK, 'center'), text: ts('inter-400', 4.5 * k, rgb('#444444'), 'center') });
      els.push(mini('prev', y), mini('next', y + mh + 4 * c.s));
      const ny = y + 2 * mh + 10 * c.s;
      if (ny + 8 * c.s <= colBottom) els.push(text('notes-title', 'Notes heading', 'subheading', R(x, ny, colW, 8 * c.s), 'NOTES', ts('montserrat-700', 10 * c.s, ACCENT, 'left', { letterSpacingPt: 1 }), 'bottom'));
      const lineGap = 9 * c.s;
      for (let i = 0, ly = ny + 8 * c.s + lineGap; ly <= colBottom; i++, ly += lineGap)
        els.push({ id: `note-line-${i + 1}`, name: `Note line ${i + 1}`, type: 'shape', rect: R(x, ly, colW, 0.3), fill: rgb('#9aa5b1'), opacity: 1, radiusMm: 0, stroke: null });
      els.push({ ...(footer(c) as TextEl), rect: R(c.left, c.bottom - footH, cw, footH) });
      return { background: null, elements: els };
    }
    case 'full-grid': {
      const area = c.area === 'custom' ? 'full' : c.area;
      const sp = split({ ...c, area }, 0.45);
      const els: Element[] = [];
      if (sp.photo) els.push(photo('photo', 0, R(c.left, sp.photo.y, cw, sp.photo.h), 2 * c.s));
      const calTop = sp.cal.y;
      els.push(heading(c, R(c.left, calTop, cw * 0.6, 16 * c.s), { sizePt: 30 * c.s }));
      els.push({ id: 'mini-prev', name: 'Previous month', type: 'mini', which: 'prev', rect: R(c.right - 58 * c.s, calTop, 27 * c.s, 22 * c.s), title: ts('inter-700', 5.5 * c.s, K_BLACK, 'center'), text: ts('inter-400', 4.5 * c.s, rgb('#444444'), 'center') });
      els.push({ id: 'mini-next', name: 'Next month', type: 'mini', which: 'next', rect: R(c.right - 27 * c.s, calTop, 27 * c.s, 22 * c.s), title: ts('inter-700', 5.5 * c.s, K_BLACK, 'center'), text: ts('inter-400', 4.5 * c.s, rgb('#444444'), 'center') });
      const gs = defaultGridStyle(c.s);
      gs.dateText.sizePt = 13 * c.s;
      gs.datePosition = 'top-right';
      gs.eventText.sizePt = 6.5 * c.s;
      els.push(grid(c, R(c.left, calTop + 25 * c.s, cw, sp.cal.h - 25 * c.s - 10 * c.s), gs));
      els.push({ id: 'legend', name: 'Legend', type: 'legend', rect: R(c.left, sp.cal.y + sp.cal.h - 9 * c.s, cw * 0.6, 9 * c.s), style: ts('inter-400', 6 * c.s, rgb('#444444'), 'left') });
      els.push(footer(c));
      els.push(logo(c));
      return { background: null, elements: els };
    }
    case 'collage': {
      const sp = split(c, 0.5);
      const els: Element[] = [];
      if (sp.photo) {
        const g = 2.5 * c.s;
        const bigW = cw * 0.62;
        els.push(photo('photo', 0, R(c.left, sp.photo.y, bigW, sp.photo.h), 2 * c.s));
        const smallW = cw - bigW - g;
        const smallH = (sp.photo.h - g) / 2;
        els.push(photo('photo-2', 1, R(c.left + bigW + g, sp.photo.y, smallW, smallH), 2 * c.s));
        els.push(photo('photo-3', 2, R(c.left + bigW + g, sp.photo.y + smallH + g, smallW, smallH), 2 * c.s));
      }
      els.push(heading(c, R(c.left, sp.cal.y, cw * 0.65, headH), { fontId: 'playfair-display-700', uppercase: false, letterSpacingPt: 0, sizePt: 26 * c.s }));
      els.push(caption(c, R(c.left + cw * 0.65, sp.cal.y + 2 * c.s, cw * 0.35, headH - 2 * c.s)));
      const gs = defaultGridStyle(c.s, 'inter-600');
      gs.header.fill = rgb('#6b4f3a');
      gs.outerBorder = null;
      gs.innerLines = { widthPt: 0.4, color: rgb('#bfb3a8') };
      els.push(grid(c, R(c.left, sp.cal.y + headH + 2 * c.s, cw, sp.cal.h - headH - 2 * c.s), gs));
      els.push(footer(c));
      els.push(logo(c));
      return { background: null, elements: els };
    }
    case 'background-photo': {
      const els: Element[] = [];
      els.push({ ...photo('photo', 0, R(0, 0, c.W, c.H)), name: 'Background photograph' });
      const sp = split({ ...c, area: c.area === 'full' ? 'full' : c.area === 'top' ? 'top' : 'bottom' }, 0.5);
      const panelY = sp.cal.y - 4 * c.s;
      const panelH = c.area === 'top' ? sp.cal.h + 8 * c.s : c.bottom + 2 * c.s - panelY;
      els.push({ id: 'panel', name: 'Calendar panel', type: 'shape', rect: R(c.left - 3 * c.s, panelY, cw + 6 * c.s, panelH), fill: WHITE, opacity: 0.88, radiusMm: 3 * c.s, stroke: null });
      els.push(heading(c, R(c.left + 2 * c.s, sp.cal.y, cw - 4 * c.s, headH), { align: 'center' }));
      const gs = defaultGridStyle(c.s);
      gs.header.fill = null;
      gs.header.text.color = K_BLACK;
      gs.outerBorder = null;
      gs.innerLines = { widthPt: 0.35, color: rgb('#9a9a9a') };
      gs.saturday.fill = rgb('#ececec');
      gs.sunday.fill = rgb('#ececec');
      els.push(grid(c, R(c.left + 2 * c.s, sp.cal.y + headH + 2 * c.s, cw - 4 * c.s, sp.cal.h - headH - 4 * c.s), gs));
      els.push(footer(c, c.area === 'top' ? WHITE : rgb('#333333')));
      return { background: null, elements: els };
    }
    case 'business': {
      // the band content sits inside the safe area, so small pages need a taller band
      const bandH = Math.max(24 * c.s, c.H - c.bottom + 16 * c.s);
      const bandY = c.H - bandH;
      const bottom = bandY - 4 * c.s;
      const top = c.top;
      const h = bottom - top;
      const photoH = c.area === 'full' ? 0 : h * 0.47;
      const els: Element[] = [];
      if (photoH) els.push(photo('photo', 0, R(c.left, top, cw, photoH), 2 * c.s));
      const calY = top + (photoH ? photoH + 4 * c.s : 0);
      els.push(heading(c, R(c.left, calY, cw * 0.65, headH)));
      els.push(caption(c, R(c.left + cw * 0.65, calY + 3 * c.s, cw * 0.35, headH - 3 * c.s)));
      const gs = defaultGridStyle(c.s);
      gs.header.fill = ACCENT;
      els.push(grid(c, R(c.left, calY + headH + 2 * c.s, cw, bottom - calY - headH - 2 * c.s), gs));
      els.push(...brandBand(c, bandY, bandH));
      return { background: null, elements: els };
    }
    case 'estate-agent': {
      const bandH = Math.max(30 * c.s, c.H - c.bottom + 22 * c.s);
      const bandY = c.H - bandH;
      const bottom = bandY - 4 * c.s;
      const h = bottom - c.top;
      const photoH = c.area === 'full' ? 0 : h * 0.46;
      const els: Element[] = [];
      if (photoH) els.push(photo('photo', 0, R(c.left, c.top, cw, photoH), 2 * c.s));
      const calY = c.top + (photoH ? photoH + 4 * c.s : 0);
      els.push(heading(c, R(c.left, calY, cw * 0.65, headH), { fontId: 'playfair-display-700', uppercase: false, letterSpacingPt: 0, sizePt: 24 * c.s }, ACCENT));
      els.push(caption(c, R(c.left + cw * 0.65, calY + 3 * c.s, cw * 0.35, headH - 3 * c.s)));
      const gs = defaultGridStyle(c.s);
      gs.header.fill = ACCENT;
      els.push(grid(c, R(c.left, calY + headH + 2 * c.s, cw, bottom - calY - headH - 2 * c.s), gs));
      els.push(...agentBand(c, bandY, bandH));
      return { background: null, elements: els };
    }
    case 'photo-top':
    default: {
      const sp = split(c, 0.5);
      const els: Element[] = [];
      if (sp.photo) {
        els.push(photo('photo', 0, R(c.left, sp.photo.y, cw, sp.photo.h)));
        els.push(caption(c, R(c.left + cw * 0.5, sp.photo.y + sp.photo.h + 1.5 * c.s, cw * 0.5, 5 * c.s)));
      }
      const calY = sp.cal.y + (sp.photo && c.area !== 'top' ? 5 * c.s : 0);
      const calH = sp.cal.h - (sp.photo && c.area !== 'top' ? 5 * c.s : 0);
      els.push(heading(c, R(c.left, calY, cw * 0.6, headH)));
      els.push(grid(c, R(c.left, calY + headH + 2 * c.s, cw, calH - headH - 2 * c.s)));
      els.push(footer(c));
      els.push(logo(c));
      return { background: null, elements: els };
    }
  }
}

function coverDesign(id: string, c: Ctx): PageDesign {
  const cw = c.right - c.left;
  const els: Element[] = [];
  if (id === 'custom-month')
    return { background: null, elements: [{ id: 'title', name: 'Title', type: 'text', role: 'heading', rect: R(c.left, c.top + (c.bottom - c.top) * 0.4, cw, 34 * c.s), text: '{YEAR}', style: ts('montserrat-700', Math.min(64 * c.s, (cw / 4.5) * PT_PER_MM), K_BLACK, 'center', { letterSpacingPt: 6 }), valign: 'middle', panel: null }] };
  if (id === 'estate-agent') {
    const bandH = 30 * c.s;
    els.push({ ...photo('photo', 0, R(0, 0, c.W, c.H * 0.62)), name: 'Your home (cover photograph)' });
    els.push({ id: 'title', name: 'Title', type: 'text', role: 'heading', rect: R(c.left, c.H * 0.62 + 6 * c.s, cw, 26 * c.s), text: 'Your home in {YEAR}', style: ts('playfair-display-700', 34 * c.s, ACCENT, 'center'), valign: 'middle', panel: null });
    els.push({ id: 'subtitle', name: 'Customer name', type: 'text', role: 'subheading', rect: R(c.left, c.H * 0.62 + 33 * c.s, cw, 10 * c.s), text: '{CUSTOMER}', style: ts('inter-400', 13 * c.s, rgb('#333333'), 'center'), valign: 'middle', panel: null });
    els.push(...agentBand(c, c.H - bandH, bandH));
    return { background: null, elements: els };
  }
  if (id === 'background-photo') {
    els.push({ ...photo('photo', 0, R(0, 0, c.W, c.H)), name: 'Cover photograph' });
    els.push({ id: 'title', name: 'Title', type: 'text', role: 'heading', rect: R(c.left, c.H * 0.62, cw, 40 * c.s), text: '{YEAR}', style: ts('montserrat-700', 72 * c.s, WHITE, 'center', { letterSpacingPt: 6 }), valign: 'middle', panel: { fill: rgb('#000000'), opacity: 0.35, radiusMm: 3 * c.s, paddingMm: 3 * c.s } });
  } else {
    els.push({ ...photo('photo', 0, R(c.left, c.top, cw, (c.bottom - c.top) * 0.68)), name: 'Cover photograph' });
    els.push({ id: 'title', name: 'Title', type: 'text', role: 'heading', rect: R(c.left, c.top + (c.bottom - c.top) * 0.7, cw, 34 * c.s), text: '{YEAR}', style: ts('montserrat-700', 64 * c.s, K_BLACK, 'center', { letterSpacingPt: 6 }), valign: 'middle', panel: null });
  }
  els.push({ id: 'subtitle', name: 'Customer name', type: 'text', role: 'subheading', rect: R(c.left, c.bottom - 30 * c.s, cw, 12 * c.s), text: '{CUSTOMER}', style: ts('playfair-display-400', 16 * c.s, id === 'background-photo' ? WHITE : rgb('#333333'), 'center'), valign: 'middle', panel: null });
  els.push({ ...logo(c), rect: R(c.left + cw / 2 - 17 * c.s, c.bottom - 14 * c.s, 34 * c.s, 11 * c.s), align: 'center' });
  return { background: null, elements: els };
}

function backingDesign(c: Ctx): PageDesign {
  const cw = c.right - c.left;
  return {
    background: null,
    elements: [
      { id: 'logo', name: 'Logo', type: 'logo', rect: R(c.left + cw / 2 - 40 * c.s, c.H / 2 - 30 * c.s, 80 * c.s, 30 * c.s), align: 'center' },
      { id: 'contact', name: 'Contact details', type: 'text', role: 'contact', rect: R(c.left, c.H / 2 + 6 * c.s, cw, 30 * c.s), text: '{CUSTOMER}\n{CONTACT}', style: ts('inter-400', 11 * c.s, K_BLACK, 'center', { lineHeight: 1.4 }), valign: 'top', panel: null },
    ],
  };
}

/** Light band across the page bottom (into the bleed) with logo and company details. */
function brandBand(c: Ctx, y: number, h: number): Element[] {
  const cw = c.right - c.left;
  const ty = y + h * 0.15, th = c.bottom - ty; // content stays inside the safe area
  return [
    { id: 'band', name: 'Brand band', type: 'shape', rect: R(0, y, c.W, c.H - y), fill: rgb('#eef1f5'), opacity: 1, radiusMm: 0, stroke: null },
    { id: 'logo', name: 'Logo', type: 'logo', rect: R(c.left, ty, cw * 0.3, th), align: 'left' },
    { id: 'company', name: 'Company name', type: 'text', role: 'contact', rect: R(c.left + cw * 0.34, ty, cw * 0.66, th * 0.45), text: '{COMPANY}', style: ts('montserrat-700', 11 * c.s, ACCENT, 'right'), valign: 'bottom', panel: null },
    { id: 'contact', name: 'Contact details', type: 'text', role: 'contact', rect: R(c.left + cw * 0.34, ty + th * 0.5, cw * 0.66, th * 0.5), text: '{PHONE}   {EMAIL}   {WEBSITE}', style: ts('inter-400', 7.5 * c.s, rgb('#333333'), 'right', { lineHeight: 1.3 }), valign: 'top', panel: null },
  ];
}

/** Coloured band with the agent's portrait (same on every page), details and agency logo. */
function agentBand(c: Ctx, y: number, h: number): Element[] {
  const cw = c.right - c.left;
  const p = h * 0.12;
  const portrait = c.bottom - (y + p); // content stays inside the safe area
  const tx = c.left + portrait + 5 * c.s;
  const logoW = cw * 0.28;
  return [
    { id: 'band', name: 'Agent band', type: 'shape', rect: R(0, y, c.W, c.H - y), fill: ACCENT, opacity: 1, radiusMm: 0, stroke: null },
    { id: 'agent-photo', name: 'Agent portrait (every page)', type: 'image', slot: 9, shared: true, rect: R(c.left, y + p, portrait, portrait), radiusMm: portrait / 2, border: { widthPt: 1.5, color: WHITE }, required: false },
    { id: 'agent-name', name: 'Agent name', type: 'text', role: 'contact', rect: R(tx, y + p, cw - portrait - logoW - 8 * c.s, portrait * 0.42), text: '{PERSON_NAME}', style: ts('montserrat-700', 12 * c.s, WHITE, 'left'), valign: 'bottom', panel: null },
    { id: 'agent-details', name: 'Agent details', type: 'text', role: 'contact', rect: R(tx, y + p + portrait * 0.46, cw - portrait - logoW - 8 * c.s, portrait * 0.54), text: '{PERSON_TITLE}\n{PHONE}   {EMAIL}', style: ts('inter-400', 7.5 * c.s, WHITE, 'left', { lineHeight: 1.35 }), valign: 'top', panel: null },
    { id: 'logo-panel', name: 'Logo panel', type: 'shape', rect: R(c.right - logoW, y + p, logoW, portrait), fill: WHITE, opacity: 1, radiusMm: 2 * c.s, stroke: null },
    { id: 'logo', name: 'Agency logo', type: 'logo', rect: R(c.right - logoW + 2 * c.s, y + p + 2 * c.s, logoW - 4 * c.s, portrait - 4 * c.s), align: 'center' },
  ];
}

// ---------------------------------------------------------------- year planners

const PT_PER_MM = 72 / 25.4;
const clampPt = (v: number, min: number, max: number) => +Math.max(min, Math.min(max, v)).toFixed(2);
/** mm of type height → pt, kept legible */
const pt = (mm: number) => clampPt(mm * PT_PER_MM, 3.5, 400);

/** Pick the column count (2, 3, 4, 6) that gives the largest legible day cells in a box. */
export function chooseYearColumns(w: number, h: number, gap: number, count = 12): number {
  const options = count === 6 ? [2, 3, 6] : count === 4 ? [1, 2, 4] : [2, 3, 4, 6];
  let best = options[1], bestScore = 0;
  for (const cols of options) {
    const rows = count / cols;
    const bw = (w - (cols - 1) * gap) / cols, bh = (h - (rows - 1) * gap) / rows;
    const score = Math.min(bw / 7, bh / 8.6);
    if (score > bestScore + 1e-6) { best = cols; bestScore = score; }
  }
  return best;
}

/** Year grid style sized from the day-cell dimensions so it reads well on any sheet. */
export function yearStyleFor(cellW: number, cellH: number, accent: Color = ACCENT): YearStyle {
  const cell = Math.min(cellW, cellH * 1.15);
  const date = clampPt(cell * 0.46 * PT_PER_MM, 3.5, 60);
  return {
    title: ts('montserrat-700', clampPt(date * 1.05, 3.5, 64), WHITE, 'center', { uppercase: true, letterSpacingPt: +(date * 0.06).toFixed(2) }),
    titleFill: accent,
    titleRadiusMm: +(cell * 0.45).toFixed(2),
    titleHeightMm: +(cell * 1.15).toFixed(2),
    weekday: ts('montserrat-600', clampPt(date * 0.8, 3, 48), rgb('#333333'), 'center'),
    weekdayLabels: 'narrow',
    date: ts('montserrat-600', date, K_BLACK, 'center'),
    saturdayColor: rgb('#1d5c96'),
    sundayColor: rgb('#1d5c96'),
    weekendFill: null,
    shadeRadiusMm: +(cell * 0.15).toFixed(2),
  };
}

function yearGrid(rect: Rect, cols: number, gap: number, accent: Color = ACCENT, count = 12): YearGridEl {
  const rows = Math.ceil(count / cols);
  const bw = (rect.w - (cols - 1) * gap) / cols;
  const bh = (rect.h - (rows - 1) * gap) / rows;
  return { id: 'year', name: 'Year calendar', type: 'yeargrid', rect, columns: cols, gapXmm: +gap.toFixed(2), gapYmm: +gap.toFixed(2), style: yearStyleFor(bw / 7, bh / 8.2, accent) };
}

function keyEl(rect: Rect, sizePt: number, align: 'left' | 'center' | 'right' = 'center'): KeyEl {
  return { id: 'key', name: 'Holiday key', type: 'key', rect, style: ts('inter-400', clampPt(sizePt, 3.5, 30), rgb('#444444'), 'left'), align };
}

function text(id: string, name: string, role: TextEl['role'], rect: Rect, value: string, style: TextStyle, valign: TextEl['valign'] = 'middle', panel: TextEl['panel'] = null): TextEl {
  return { id, name, type: 'text', role, rect, text: value, style, valign, panel };
}

function yearDesign(id: string, c: Ctx): PageDesign {
  const cw = c.right - c.left, ch = c.bottom - c.top;
  const u = Math.min(c.W, c.H) / 100; // unit: 1% of the short side, in mm
  const landscape = c.W > c.H * 1.15;
  const gap = Math.max(1.2, u * 2.2);
  const els: Element[] = [];
  const grid = (r: Rect) => yearGrid(r, chooseYearColumns(r.w, r.h, gap), gap);

  // Business-card sizes: every template uses one slim header row so 12 months stay legible.
  if (Math.min(c.W, c.H) < 75) {
    const g = Math.max(0.8, u * 1.6);
    const hh = landscape ? ch * 0.17 : ch * 0.14;
    els.push({ id: 'logo', name: 'Logo', type: 'logo', rect: R(c.left, c.top, cw * 0.38, hh), align: 'left' });
    els.push(text('heading', 'Year heading', 'heading', R(c.left + cw * 0.38, c.top, cw * 0.24, hh), '{YEAR}', ts('montserrat-700', pt(Math.min(hh * 0.55, (cw * 0.24) / 3)), ACCENT, 'center')));
    els.push(text('contact', 'Contact details', 'contact', R(c.left + cw * 0.62, c.top, cw * 0.38, hh), '{PHONE}\n{WEBSITE}', ts('inter-600', pt(hh * 0.22), rgb('#333333'), 'right', { lineHeight: 1.15 })));
    const y = c.top + hh + g;
    const keyH = Math.max(2.2, u * 3.5);
    els.push(yearGrid(R(c.left, y, cw, c.bottom - keyH - g - y), chooseYearColumns(cw, c.bottom - keyH - g - y, g), g));
    els.push(keyEl(R(c.left, c.bottom - keyH, cw, keyH), pt(keyH * 0.45), 'center'));
    return { background: null, elements: els };
  }

  if (id === 'custom-year') {
    const hh = Math.max(u * 8, ch * 0.08);
    els.push(text('heading', 'Year heading', 'heading', R(c.left, c.top, cw, hh), '{YEAR}', ts('montserrat-700', pt(Math.min(hh * 0.7, cw / 4)), ACCENT, 'center', { letterSpacingPt: 2 })));
    const y = c.top + hh + gap, keyH = u * 4;
    els.push(grid(R(c.left, y, cw, c.bottom - keyH - gap - y)));
    els.push(keyEl(R(c.left, c.bottom - keyH, cw, keyH), pt(u * 2.4), 'center'));
    return { background: null, elements: els };
  }

  if (id === 'year-photo') {
    if (landscape) {
      const pw = c.W * 0.32;
      els.push({ ...photo('photo', 0, R(0, 0, pw, c.H)), name: 'Header photograph' });
      els.push(text('company', 'Company name', 'heading', R(c.left, c.H * 0.08, pw - c.left - gap, u * 14), '{COMPANY}', ts('montserrat-700', pt(u * 5), WHITE, 'center'), 'middle', { fill: rgb('#000000'), opacity: 0.35, radiusMm: u, paddingMm: u }));
      const x = pw + gap * 2, w = c.right - x;
      els.push(text('heading', 'Year heading', 'heading', R(x, c.top, w, u * 12), '{YEAR} CALENDAR', ts('montserrat-700', pt(u * 7), K_BLACK, 'left', { letterSpacingPt: 1 })));
      els.push(grid(R(x, c.top + u * 14, w, ch - u * 14 - u * 12)));
      els.push(keyEl(R(x, c.bottom - u * 11, w * 0.5, u * 4), pt(u * 2.6), 'left'));
      els.push(text('contact', 'Contact details', 'contact', R(x + w * 0.5, c.bottom - u * 11, w * 0.5, u * 10), '{CONTACT}', ts('inter-400', pt(u * 2.6), rgb('#333333'), 'right', { lineHeight: 1.3 })));
      return { background: null, elements: els };
    }
    const ph = c.H * 0.24, bandH = c.H * 0.075;
    els.push({ ...photo('photo', 0, R(0, 0, c.W, ph)), name: 'Header photograph' });
    els.push(text('company', 'Company name', 'heading', R(c.left, ph * 0.3, cw, ph * 0.45), '{COMPANY}', ts('montserrat-700', pt(u * 6), WHITE, 'center', { uppercase: true }), 'middle', { fill: rgb('#000000'), opacity: 0.3, radiusMm: u, paddingMm: u * 1.5 }));
    const y = ph + gap;
    els.push(text('heading', 'Year heading', 'heading', R(c.left, y, cw, u * 10), '{YEAR} CALENDAR', ts('montserrat-700', pt(u * 6.5), K_BLACK, 'center', { letterSpacingPt: 1 })));
    const gy = y + u * 11, keyH = u * 4;
    const bandY = c.H - bandH;
    els.push(grid(R(c.left, gy, cw, bandY - gap - keyH - gap - gy)));
    els.push(keyEl(R(c.left, bandY - gap - keyH, cw, keyH), pt(u * 2.4), 'left'));
    els.push({ id: 'band', name: 'Contact band', type: 'shape', rect: R(0, bandY, c.W, c.H - bandY), fill: ACCENT, opacity: 1, radiusMm: 0, stroke: null });
    els.push(text('contact', 'Contact details', 'contact', R(c.left, bandY + bandH * 0.1, cw, Math.max(u * 3, Math.min(bandH * 0.8, c.bottom - bandY - bandH * 0.1))), '{CONTACT}', ts('montserrat-600', pt(u * 3.2), WHITE, 'center', { lineHeight: 1.25 })));
    return { background: null, elements: els };
  }

  if (id === 'year-estate-agent') {
    const hb = landscape ? c.H * 0.26 : c.H * 0.17;
    const p = hb * 0.14, portrait = hb - 2 * p;
    els.push({ id: 'band', name: 'Agent band', type: 'shape', rect: R(0, 0, c.W, hb), fill: ACCENT, opacity: 1, radiusMm: 0, stroke: null });
    const top = Math.max(c.top, p);
    const pSize = Math.min(portrait, hb - top - p);
    els.push({ id: 'agent-photo', name: 'Agent portrait', type: 'image', slot: 1, rect: R(c.left, top, pSize, pSize), radiusMm: pSize / 2, border: { widthPt: 1, color: WHITE }, required: false });
    const logoW = cw * 0.28;
    const tx = c.left + pSize + u * 3, tw = cw - pSize - logoW - u * 6;
    els.push(text('agent-name', 'Agent name', 'contact', R(tx, top, tw, pSize * 0.42), '{PERSON_NAME}', ts('montserrat-700', pt(Math.min(u * 4.2, pSize * 0.42 * 0.65)), WHITE, 'left'), 'bottom'));
    els.push(text('agent-details', 'Agent details', 'contact', R(tx, top + pSize * 0.46, tw, pSize * 0.54), '{PERSON_TITLE}\n{PHONE}\n{EMAIL}', ts('inter-400', pt(Math.min(u * 2.5, (pSize * 0.54) / (3 * 1.4))), WHITE, 'left', { lineHeight: 1.3 }), 'top'));
    els.push({ id: 'logo-panel', name: 'Logo panel', type: 'shape', rect: R(c.right - logoW, top, logoW, pSize), fill: WHITE, opacity: 1, radiusMm: u, stroke: null });
    els.push({ id: 'logo', name: 'Agency logo', type: 'logo', rect: R(c.right - logoW + u, top + u, logoW - 2 * u, pSize - 2 * u), align: 'center' });
    let y = hb + gap;
    if (!landscape) {
      const homeH = c.H * 0.17;
      els.push({ id: 'photo', name: 'Your home (optional)', type: 'image', slot: 0, rect: R(c.left, y, cw, homeH), radiusMm: u, border: null, required: false });
      els.push(text('caption', 'Photo caption', 'caption', R(c.left + u, y + homeH - u * 7, cw - 2 * u, u * 6), '{CAPTION}', ts('playfair-display-700', pt(u * 3.2), WHITE, 'left'), 'middle', { fill: rgb('#000000'), opacity: 0.35, radiusMm: u * 0.6, paddingMm: u * 0.8 }));
      y += homeH + gap;
    }
    els.push(text('heading', 'Year heading', 'heading', R(c.left, y, cw, u * 8), '{YEAR}', ts('playfair-display-700', pt(u * 6), ACCENT, 'center')));
    y += u * 9;
    const footH = u * 5, keyH = u * 4;
    els.push(grid(R(c.left, y, cw, c.bottom - footH - keyH - gap * 2 - y)));
    els.push(keyEl(R(c.left, c.bottom - footH - keyH - gap, cw, keyH), pt(u * 2.4), 'left'));
    els.push(text('footer', 'Agency address & website', 'footer', R(c.left, c.bottom - footH, cw, footH), '{COMPANY}   {ADDRESS}   {WEBSITE}', ts('inter-400', pt(u * 2.4), rgb('#444444'), 'center')));
    return { background: null, elements: els };
  }

  if (id === 'year-personal') {
    if (landscape) {
      const pw = cw * 0.38;
      els.push({ ...photo('photo', 0, R(c.left, c.top, pw, ch)), radiusMm: u * 1.5, name: 'Family photograph' });
      const x = c.left + pw + gap * 2, w = c.right - x;
      els.push(text('title', 'Name', 'heading', R(x, c.top, w * 0.7, u * 13), '{CUSTOMER}', ts('dancing-script-700', pt(u * 8), ACCENT, 'left'), 'bottom'));
      els.push(text('heading', 'Year heading', 'heading', R(x + w * 0.7, c.top, w * 0.3, u * 13), '{YEAR}', ts('montserrat-700', pt(u * 7), K_BLACK, 'right'), 'bottom'));
      els.push(grid(R(x, c.top + u * 15, w, ch - u * 15 - u * 5)));
      els.push(keyEl(R(x, c.bottom - u * 4, w, u * 4), pt(u * 2.4), 'left'));
      return { background: null, elements: els };
    }
    const ph = ch * 0.36;
    els.push({ ...photo('photo', 0, R(c.left, c.top, cw, ph)), radiusMm: u * 1.5, name: 'Family photograph' });
    let y = c.top + ph + gap;
    els.push(text('title', 'Name', 'heading', R(c.left, y, cw * 0.68, u * 12), '{CUSTOMER}', ts('dancing-script-700', pt(u * 8), ACCENT, 'left'), 'bottom'));
    els.push(text('heading', 'Year heading', 'heading', R(c.left + cw * 0.68, y, cw * 0.32, u * 12), '{YEAR}', ts('montserrat-700', pt(u * 7), K_BLACK, 'right'), 'bottom'));
    y += u * 13;
    els.push(grid(R(c.left, y, cw, c.bottom - u * 5 - y)));
    els.push(keyEl(R(c.left, c.bottom - u * 4, cw, u * 4), pt(u * 2.4), 'left'));
    return { background: null, elements: els };
  }

  if (id === 'year-poster') {
    const hh = landscape ? ch * 0.13 : ch * 0.1;
    els.push({ id: 'logo', name: 'Logo', type: 'logo', rect: R(c.left, c.top, cw * 0.26, hh), align: 'left' });
    els.push(text('heading', 'Year heading', 'heading', R(c.left + cw * 0.28, c.top, cw * 0.44, hh), '{YEAR}', ts('montserrat-700', pt(hh * 0.7), ACCENT, 'center', { letterSpacingPt: +(pt(hh * 0.7) * 0.08).toFixed(1) })));
    els.push(text('contact', 'Contact details', 'contact', R(c.left + cw * 0.74, c.top, cw * 0.26, hh), '{COMPANY}\n{PHONE}\n{WEBSITE}', ts('inter-600', pt(hh * 0.16), rgb('#333333'), 'right', { lineHeight: 1.3 })));
    const y = c.top + hh + gap * 1.5;
    const keyH = u * 3.5;
    els.push(grid(R(c.left, y, cw, c.bottom - keyH - gap - y)));
    els.push(keyEl(R(c.left, c.bottom - keyH, cw, keyH), pt(u * 2.2), 'center'));
    return { background: null, elements: els };
  }

  // year-business (default)
  if (landscape) {
    const lw = cw * 0.3;
    els.push({ id: 'logo', name: 'Logo', type: 'logo', rect: R(c.left, c.top, lw, ch * 0.3), align: 'center' });
    els.push(text('tagline', 'Tagline', 'subheading', R(c.left, c.top + ch * 0.31, lw, ch * 0.1), '{TAGLINE}', ts('montserrat-600', pt(u * 3), ACCENT, 'center', { uppercase: true, lineHeight: 1.1 })));
    els.push(text('phone', 'Phone', 'contact', R(c.left, c.top + ch * 0.44, lw, ch * 0.13), '{PHONE}', ts('montserrat-700', pt(u * 4.5), WHITE, 'center'), 'middle', { fill: ACCENT, opacity: 1, radiusMm: ch * 0.065, paddingMm: u * 0.5 }));
    els.push(text('web', 'Web & email', 'contact', R(c.left, c.top + ch * 0.6, lw, ch * 0.16), '{WEBSITE}\n{EMAIL}', ts('inter-400', pt(u * 2.8), rgb('#333333'), 'center', { lineHeight: 1.3 })));
    els.push(text('heading', 'Year heading', 'heading', R(c.left, c.top + ch * 0.8, lw, ch * 0.2), '{YEAR}', ts('montserrat-700', pt(u * 9), ACCENT, 'center', { letterSpacingPt: 2 })));
    const x = c.left + lw + gap * 2;
    els.push(grid(R(x, c.top, c.right - x, ch - u * 5)));
    els.push(keyEl(R(x, c.bottom - u * 4, c.right - x, u * 4), pt(u * 2.4), 'left'));
    return { background: null, elements: els };
  }
  const photoH = c.H * 0.13;
  let y = c.top;
  els.push({ id: 'logo', name: 'Logo', type: 'logo', rect: R(c.left, y, cw, ch * 0.12), align: 'center' });
  y += ch * 0.12 + gap * 0.5;
  els.push(text('tagline', 'Tagline', 'subheading', R(c.left, y, cw, u * 4), '{TAGLINE}', ts('montserrat-600', pt(u * 3), ACCENT, 'center', { uppercase: true, letterSpacingPt: 0.5 })));
  y += u * 5;
  els.push(text('phone', 'Phone', 'contact', R(c.left + cw * 0.06, y, cw * 0.88, u * 8), '{PHONE}', ts('montserrat-700', pt(u * 5.5), WHITE, 'center', { letterSpacingPt: 1 }), 'middle', { fill: ACCENT, opacity: 1, radiusMm: u * 4, paddingMm: u * 0.5 }));
  y += u * 9.5;
  els.push(text('web', 'Web & email', 'contact', R(c.left, y, cw, u * 4), '{WEBSITE}     {EMAIL}', ts('inter-400', pt(u * 3), rgb('#333333'), 'center')));
  y += u * 5;
  els.push(text('heading', 'Year heading', 'heading', R(c.left, y, cw, u * 10), '{YEAR}', ts('montserrat-700', pt(u * 8), ACCENT, 'center', { letterSpacingPt: 3 })));
  y += u * 11;
  const keyH = u * 4;
  const gridBottom = c.H - photoH - gap - keyH - gap;
  els.push(grid(R(c.left, y, cw, gridBottom - y)));
  els.push(keyEl(R(c.left, gridBottom + gap, cw, keyH), pt(u * 2.4), 'left'));
  els.push({ id: 'photo', name: 'Photo strip (optional)', type: 'image', slot: 0, rect: R(0, c.H - photoH, c.W, photoH), radiusMm: 0, border: null, required: false });
  return { background: null, elements: els };
}

/** Small pages (tear-off pad leaves, business-card sizes): heading, logo, grid, one contact line. */
function compactMonth(id: string, c: Ctx): PageDesign {
  const cw = c.right - c.left, ch = c.bottom - c.top;
  const hh = ch * 0.16, footH = ch * 0.09, g = Math.max(0.8, ch * 0.02);
  const els: Element[] = [];
  els.push(heading(c, R(c.left, c.top, cw * 0.6, hh), { sizePt: pt(Math.min(hh * 0.62, (cw * 0.6) / 11)), letterSpacingPt: 0.3 }, id === 'estate-agent' || id === 'business' || id === 'desk-pad' ? ACCENT : K_BLACK));
  els.push({ id: 'logo', name: 'Logo', type: 'logo', rect: R(c.left + cw * 0.62, c.top, cw * 0.38, hh), align: 'right' });
  const gy = c.top + hh + g, gh = c.bottom - footH - g - gy;
  const cellH = (gh * 0.86) / 6, cellW = cw / 7;
  const cell = Math.min(cellW, cellH * 1.6);
  const gs = defaultGridStyle(c.s);
  gs.header = { ...gs.header, heightMm: +(gh * 0.14).toFixed(2), fill: ACCENT, text: { ...gs.header.text, sizePt: clampPt(gh * 0.14 * 0.5 * PT_PER_MM, 3, 12), letterSpacingPt: 0.2 } };
  gs.cellPaddingMm = +(cell * 0.08).toFixed(2);
  gs.dateText = { ...gs.dateText, sizePt: clampPt(cellH * 0.42 * PT_PER_MM, 3, 14) };
  gs.adjacentText = { ...gs.adjacentText, sizePt: clampPt(cellH * 0.38 * PT_PER_MM, 3, 12) };
  gs.eventText = { ...gs.eventText, sizePt: 3 };
  gs.outerBorder = { widthPt: 0.4, color: K_BLACK };
  gs.innerLines = { widthPt: 0.25, color: rgb('#9a9a9a') };
  els.push(grid(c, R(c.left, gy, cw, gh), gs));
  els.push(text('footer', 'Footer', 'footer', R(c.left, c.bottom - footH, cw, footH), '{PHONE}   {WEBSITE}', ts('inter-600', clampPt(footH * 0.55 * PT_PER_MM, 3, 10), rgb('#333333'), 'center')));
  return { background: null, elements: els };
}


/** One face of a folded tent card: six months (Jan–Jun or Jul–Dec, from the page) beside the branding. */
function faceDesign(id: string, c: Ctx, count = 6): PageDesign {
  const cw = c.right - c.left, ch = c.bottom - c.top;
  const u = Math.min(c.W, c.H) / 100;
  const gap = Math.max(1.2, u * 2.4);
  const els: Element[] = [];
  const g6 = (r: Rect) => yearGrid(r, chooseYearColumns(r.w, r.h, gap, count), gap, ACCENT, count);
  const landscape = c.W > c.H * 1.1;
  if (id === 'custom-half') {
    const hh = Math.max(3.5, ch * 0.12), keyH = Math.max(2.5, u * 4);
    els.push(text('heading', 'Year heading', 'heading', R(c.left, c.top, cw, hh), '{YEAR}', ts('montserrat-700', pt(Math.min(hh * 0.7, cw / 5)), ACCENT, 'center')));
    const y = c.top + hh + gap;
    els.push(g6(R(c.left, y, cw, c.bottom - keyH - gap - y)));
    els.push(keyEl(R(c.left, c.bottom - keyH, cw, keyH), pt(keyH * 0.6), 'center'));
    return { background: null, elements: els };
  }
  if (id === 'tent-photo') {
    const footH = Math.max(3, u * 7);
    if (landscape) {
      const pw = c.W * 0.36;
      els.push({ ...photo('photo', 0, R(0, 0, pw, c.H)), name: 'Photograph' });
      const x = pw + gap * 2, w = c.right - x;
      els.push(text('heading', 'Year heading', 'heading', R(x, c.top, w, u * 11), '{YEAR}', ts('montserrat-700', pt(u * 8), ACCENT, 'right', { letterSpacingPt: 1 }), 'bottom'));
      const y = c.top + u * 12;
      els.push(g6(R(x, y, w, c.bottom - footH - gap - y)));
      els.push(text('contact', 'Contact details', 'contact', R(x, c.bottom - footH, w, footH), '{PHONE}   {WEBSITE}', ts('inter-600', pt(footH * 0.5), rgb('#333333'), 'right')));
      return { background: null, elements: els };
    }
    const ph = c.H * 0.36;
    els.push({ ...photo('photo', 0, R(0, 0, c.W, ph)), name: 'Photograph' });
    const y = ph + gap;
    els.push(g6(R(c.left, y, cw, c.bottom - footH - gap - y)));
    els.push(text('contact', 'Contact details', 'contact', R(c.left, c.bottom - footH, cw, footH), '{PHONE}   {WEBSITE}', ts('inter-600', pt(footH * 0.5), rgb('#333333'), 'center')));
    return { background: null, elements: els };
  }
  // tent-business (default)
  if (landscape) {
    const lw = cw * 0.3;
    els.push({ id: 'logo', name: 'Logo', type: 'logo', rect: R(c.left, c.top, lw, ch * 0.3), align: 'center' });
    els.push(text('tagline', 'Tagline', 'subheading', R(c.left, c.top + ch * 0.32, lw, ch * 0.1), '{TAGLINE}', ts('montserrat-600', pt(Math.min(u * 3, ch * 0.05)), ACCENT, 'center', { uppercase: true, lineHeight: 1.1 })));
    els.push(text('phone', 'Phone', 'contact', R(c.left, c.top + ch * 0.45, lw, ch * 0.13), '{PHONE}', ts('montserrat-700', pt(Math.min(u * 4.2, lw / 9)), WHITE, 'center'), 'middle', { fill: ACCENT, opacity: 1, radiusMm: ch * 0.065, paddingMm: u * 0.5 }));
    els.push(text('web', 'Web & email', 'contact', R(c.left, c.top + ch * 0.61, lw, ch * 0.16), '{WEBSITE}\n{EMAIL}', ts('inter-400', pt(Math.min(u * 2.6, lw / 22)), rgb('#333333'), 'center', { lineHeight: 1.3 })));
    els.push(text('heading', 'Year heading', 'heading', R(c.left, c.top + ch * 0.8, lw, ch * 0.2), '{YEAR}', ts('montserrat-700', pt(Math.min(u * 8, ch * 0.13)), ACCENT, 'center', { letterSpacingPt: 2 })));
    const x = c.left + lw + gap * 2;
    els.push(g6(R(x, c.top, c.right - x, ch - u * 5)));
    els.push(keyEl(R(x, c.bottom - u * 4, c.right - x, u * 4), pt(u * 2.4), 'left'));
    return { background: null, elements: els };
  }
  const hh = ch * 0.16;
  els.push({ id: 'logo', name: 'Logo', type: 'logo', rect: R(c.left, c.top, cw * 0.4, hh), align: 'left' });
  els.push(text('heading', 'Year heading', 'heading', R(c.left + cw * 0.4, c.top, cw * 0.2, hh), '{YEAR}', ts('montserrat-700', pt(Math.min(hh * 0.55, cw * 0.06)), ACCENT, 'center')));
  els.push(text('contact', 'Contact details', 'contact', R(c.left + cw * 0.6, c.top, cw * 0.4, hh), '{PHONE}\n{WEBSITE}', ts('inter-600', pt(Math.min(hh * 0.22, cw * 0.025)), rgb('#333333'), 'right', { lineHeight: 1.2 })));
  const y = c.top + hh + gap, keyH = u * 4;
  els.push(g6(R(c.left, y, cw, c.bottom - keyH - gap - y)));
  els.push(keyEl(R(c.left, c.bottom - keyH, cw, keyH), pt(u * 2.4), 'center'));
  return { background: null, elements: els };
}

/** Magnetic carrier card for a tear-off pad: branding in the free space, the pad zone left blank. */
function carrierDesign(id: string, c: Ctx, pad: { x: number; y: number; w: number; h: number }): PageDesign {
  const cw = c.right - c.left;
  const u = Math.min(c.W, c.H) / 100;
  const g = Math.max(1.5, u * 3);
  const els: Element[] = [];
  const hasPad = pad.w > 0 && pad.h > 0;
  const above = { y: c.top, h: (hasPad ? pad.y - g : c.bottom) - c.top };
  const below = { y: hasPad ? pad.y + pad.h + g : c.bottom, h: hasPad ? c.bottom - (pad.y + pad.h + g) : 0 };
  const main = above.h >= below.h ? above : below;
  const other = main === above ? below : above;
  const H = main.h;
  if (H < 10) return { background: null, elements: [{ ...logo(c), rect: R(c.left, main.y, cw, Math.max(1, H)), align: 'center' }] };
  const fs = (share: number, chars: number) => pt(Math.min(H * share * 0.55, cw / (chars * 0.55)));
  if (id === 'estate-agent' || id === 'year-estate-agent') {
    const ps = Math.min(H * 0.42, cw * 0.45);
    let y = main.y;
    els.push({ id: 'agent-photo', name: 'Agent portrait', type: 'image', slot: 9, shared: true, rect: R(c.left + cw / 2 - ps / 2, y, ps, ps), radiusMm: ps / 2, border: { widthPt: 1, color: ACCENT }, required: false });
    y += ps + H * 0.03;
    els.push(text('agent-name', 'Agent name', 'contact', R(c.left, y, cw, H * 0.1), '{PERSON_NAME}', ts('montserrat-700', fs(0.1, 18), ACCENT, 'center')));
    y += H * 0.1;
    els.push(text('agent-details', 'Agent details', 'contact', R(c.left, y, cw, H * 0.18), '{PERSON_TITLE}\n{PHONE}\n{EMAIL}', ts('inter-400', fs(0.06, 30), rgb('#333333'), 'center', { lineHeight: 1.25 }), 'top'));
    y += H * 0.2;
    els.push({ id: 'logo', name: 'Agency logo', type: 'logo', rect: R(c.left, y, cw, main.y + H - y), align: 'center' });
  } else {
    const parts: [string, number][] = [['logo', 0.32], ['company', 0.12], ['tagline', 0.08], ['phone', 0.16], ['web', 0.16]];
    let y = main.y;
    const gap = H * 0.04;
    const avail = H - gap * (parts.length - 1);
    for (const [k, share] of parts) {
      const h = avail * share / 0.84;
      const r = R(c.left, y, cw, h);
      if (k === 'logo') els.push({ id: 'logo', name: 'Logo', type: 'logo', rect: r, align: 'center' });
      if (k === 'company') els.push(text('company', 'Company name', 'heading', r, '{COMPANY}', ts('montserrat-700', fs(share * 1.1, 20), ACCENT, 'center')));
      if (k === 'tagline') els.push(text('tagline', 'Tagline', 'subheading', r, '{TAGLINE}', ts('montserrat-600', fs(share * 0.9, 32), rgb('#444444'), 'center', { uppercase: true })));
      if (k === 'phone') els.push(text('phone', 'Phone', 'contact', R(c.left + cw * 0.06, y, cw * 0.88, h), '{PHONE}', ts('montserrat-700', fs(share * 0.75, 15), WHITE, 'center'), 'middle', { fill: ACCENT, opacity: 1, radiusMm: h / 2, paddingMm: u * 0.5 }));
      if (k === 'web') els.push(text('web', 'Web & email', 'contact', r, '{WEBSITE}\n{EMAIL}', ts('inter-400', fs(share * 0.38, 30), rgb('#333333'), 'center', { lineHeight: 1.3 })));
      y += h + gap;
    }
  }
  if (other.h >= 5) els.push(text('address', 'Address', 'footer', R(c.left, other.y, cw, Math.min(other.h, 8 * u)), '{ADDRESS}', ts('inter-400', pt(Math.min(other.h, 8 * u) * 0.35), rgb('#555555'), 'center')));
  return { background: null, elements: els };
}

/** A tent's information panel (base / side): a heading and the public holiday list. */
function infoDesign(c: Ctx): PageDesign {
  const cw = c.right - c.left, ch = c.bottom - c.top;
  const landscape = c.W > c.H * 1.1;
  const hh = Math.min(ch * 0.16, 9);
  const g = Math.max(1, ch * 0.03);
  const listH = ch - hh - g;
  const lines = landscape ? 7 : 16;
  const list: HolidayListEl = {
    id: 'holidays', name: 'Holiday & event list', type: 'holidays', kinds: ['public'], rect: R(c.left, c.top + hh + g, cw, listH),
    style: ts('inter-400', clampPt(((listH * 0.85) / lines / 1.3) * PT_PER_MM, 3.5, 9), rgb('#333333'), 'left', { lineHeight: 1.3 }),
    layout: 'column', columns: landscape ? 2 : 1, dateFormat: 'short-month', names: 'short', range: 'year', observed: 'marker',
    marker: '*', separator: ' | ', footnote: '* Sunday public holiday observed on the Monday',
  };
  return {
    background: null,
    elements: [text('heading', 'Heading', 'heading', R(c.left, c.top, cw, hh), 'Public holidays {YEAR}', ts('montserrat-700', clampPt(hh * 0.6 * PT_PER_MM, 3.5, 14), ACCENT, 'left')), list],
  };
}

export const templateDefaultArea = (id: string): Product['calendarArea'] => TEMPLATES.find((t) => t.id === id)?.defaultArea ?? 'bottom';

export function buildTemplate(
  templateId: string,
  size: PageSize,
  product: TemplateProduct,
): { month: PageDesign; cover: PageDesign; backing: PageDesign; year: PageDesign; face: PageDesign; carrier: PageDesign; info: PageDesign } {
  const c = ctxFor(size, product);
  const info = templateInfo(templateId);
  // Every project carries all designs; the template's own product gets the chosen layout.
  const monthId = info.product === 'monthly' ? templateId : 'photo-top';
  const yearId = info.product === 'year' ? templateId : 'year-business';
  const mc = info.product === 'monthly' ? c : ctxFor(size, { ...product, calendarArea: 'bottom' });
  const tent = product.tent ?? { baseMm: 42, tabMm: 15, layout: 'classic' as const };
  const flat = { widthMm: size.w, heightMm: size.h, tent };
  // non-tent products carry a default A4 tent face so switching product starts from a sane design
  const first = info.product === 'tent' ? tentPagePanels(flat)[0] : null;
  const face = first ? { w: first.w, h: Math.max(40, first.h) } : { w: 210, h: 120 };
  const infoPanel = info.product === 'tent' ? tentFlat(flat).panels.find((p) => p.key === 'info') : undefined;
  const ic = ctxFor(infoPanel ? { w: infoPanel.w, h: infoPanel.h } : { w: 210, h: 99 }, { ...product, bindingEdge: 'none', bindingClearanceMm: 0, calendarArea: 'full' });
  const months = tent.layout === 'dl-portrait-4' ? 4 : 6;
  const faceId = info.product === 'tent' ? templateId : info.audience === 'personal' ? 'tent-photo' : 'tent-business';
  const fc = ctxFor(face, { ...product, bindingEdge: 'none', bindingClearanceMm: 0, calendarArea: 'full' });
  const car = product.carrier ?? { enabled: false, widthMm: 99, heightMm: 210, pad: { x: 7, y: 148, w: 85, h: 55 } };
  const cc = ctxFor({ w: car.widthMm, h: car.heightMm }, { ...product, bindingEdge: 'none', bindingClearanceMm: 0, calendarArea: 'full' });
  return {
    month: monthDesign(monthId, mc), cover: coverDesign(monthId, mc), backing: backingDesign(c), year: yearDesign(yearId, c),
    face: faceDesign(faceId, fc, months), carrier: carrierDesign(templateId, cc, car.pad), info: infoDesign(ic),
  };
}

/**
 * Re-position elements to a freshly built template geometry while keeping the
 * user's styles and text. Elements that the new geometry no longer contains
 * are kept unless `dropMissing` (used when the calendar area removes the photo).
 */
export function relayoutDesign(current: PageDesign, fresh: PageDesign): PageDesign {
  const freshById = new Map(fresh.elements.map((e) => [e.id, e]));
  const kept = current.elements.filter((e) => freshById.has(e.id)).map((e) => ({ ...e, rect: freshById.get(e.id)!.rect }) as Element);
  const keptIds = new Set(kept.map((e) => e.id));
  const added = fresh.elements.filter((e) => !keptIds.has(e.id));
  // keep the template's z-order
  const order = new Map(fresh.elements.map((e, i) => [e.id, i]));
  return { ...current, elements: [...kept, ...added].sort((a, b) => order.get(a.id)! - order.get(b.id)!) };
}

/** Proportionally scale a design to a new sheet size (rects and type). */
export function scaleDesign(d: PageDesign, from: PageSize, to: PageSize): PageDesign {
  const sx = to.w / from.w, sy = to.h / from.h;
  const st = Math.sqrt(sx * sy);
  const scaleStyle = (t: TextStyle): TextStyle => ({ ...t, sizePt: +(t.sizePt * st).toFixed(2), letterSpacingPt: +(t.letterSpacingPt * st).toFixed(2) });
  return {
    ...d,
    elements: d.elements.map((e) => {
      const rect = R(e.rect.x * sx, e.rect.y * sy, e.rect.w * sx, e.rect.h * sy);
      switch (e.type) {
        case 'text': return { ...e, rect, style: scaleStyle(e.style) };
        case 'legend': return { ...e, rect, style: scaleStyle(e.style) };
        case 'key': return { ...e, rect, style: scaleStyle(e.style) };
        case 'holidays': return { ...e, rect, style: scaleStyle(e.style) };
        case 'yeargrid': {
          const y = e.style;
          return {
            ...e, rect, gapXmm: +(e.gapXmm * sx).toFixed(2), gapYmm: +(e.gapYmm * sy).toFixed(2),
            style: { ...y, title: scaleStyle(y.title), weekday: scaleStyle(y.weekday), date: scaleStyle(y.date), titleHeightMm: +(y.titleHeightMm * st).toFixed(2), titleRadiusMm: +(y.titleRadiusMm * st).toFixed(2), shadeRadiusMm: +(y.shadeRadiusMm * st).toFixed(2) },
          };
        }
        case 'mini': return { ...e, rect, title: scaleStyle(e.title), text: scaleStyle(e.text) };
        case 'grid': {
          const g = e.style;
          return {
            ...e, rect,
            style: {
              ...g,
              cellPaddingMm: +(g.cellPaddingMm * st).toFixed(2),
              header: { ...g.header, heightMm: +(g.header.heightMm * st).toFixed(2), text: scaleStyle(g.header.text) },
              dateText: scaleStyle(g.dateText), adjacentText: scaleStyle(g.adjacentText), eventText: scaleStyle(g.eventText),
              weekNumbers: { ...g.weekNumbers, widthMm: +(g.weekNumbers.widthMm * st).toFixed(2), text: scaleStyle(g.weekNumbers.text) },
            },
          };
        }
        default: return { ...e, rect };
      }
    }),
  };
}
